import { useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  ACCESS_LABEL,
  directReports,
  initials,
  isExecutiveUser,
  type Directory,
  type User,
} from '@gdec/shared';
import { EmptyState } from '@/components/EmptyState';

function matchesSearch(user: User, search: string): boolean {
  if (!search) return true;
  const q = search.toLowerCase();
  return (
    user.name.toLowerCase().includes(q) ||
    user.email.toLowerCase().includes(q) ||
    user.position.toLowerCase().includes(q)
  );
}

/**
 * Executives never appear nested under a Manager or Lead — they are always their
 * own root, matching how the approval chain treats them.
 */
function childrenOf(directory: Directory, user: User, scopeDept: string): User[] {
  return directReports(directory, user.id).filter(
    (k) => (!scopeDept || k.department === scopeDept) && !isExecutiveUser(k),
  );
}

function subtreeMatches(
  directory: Directory,
  user: User,
  scopeDept: string,
  search: string,
  seen: ReadonlySet<string>,
): boolean {
  if (matchesSearch(user, search)) return true;
  if (seen.has(user.id)) return false;
  const next = new Set(seen).add(user.id);
  return childrenOf(directory, user, scopeDept).some((k) =>
    subtreeMatches(directory, k, scopeDept, search, next),
  );
}

interface TreeContext {
  directory: Directory;
  scopeDept: string;
  search: string;
  collapsed: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onEdit: (user: User) => void;
  onReparent: (employeeId: string, managerId: string) => void;
  dragId: RefObject<string | null>;
  dragOverId: string | null;
  setDragOverId: (id: string | null) => void;
}

function OrgNode({
  user,
  ctx,
  seen,
}: {
  user: User;
  ctx: TreeContext;
  seen: ReadonlySet<string>;
}): ReactNode {
  const allKids = childrenOf(ctx.directory, user, ctx.scopeDept);
  const visibleKids = ctx.search
    ? allKids.filter((k) => subtreeMatches(ctx.directory, k, ctx.scopeDept, ctx.search, seen))
    : allKids;

  if (ctx.search && !matchesSearch(user, ctx.search) && visibleKids.length === 0) return null;

  const collapsed = ctx.collapsed.has(user.id);
  const hasKids = allKids.length > 0;
  // Imported data can contain a reporting loop the editor would refuse to make;
  // the visited set stops it from becoming an infinite render.
  const nextSeen = new Set(seen).add(user.id);

  return (
    <div className="tree-node">
      <div
        className={ctx.dragOverId === user.id ? 'tree-card dragover' : 'tree-card'}
        draggable
        onDragStart={() => {
          ctx.dragId.current = user.id;
        }}
        onDragEnd={() => {
          ctx.dragId.current = null;
          ctx.setDragOverId(null);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          ctx.setDragOverId(user.id);
        }}
        onDragLeave={() => ctx.setDragOverId(null)}
        onDrop={(e) => {
          e.preventDefault();
          ctx.setDragOverId(null);
          const dragged = ctx.dragId.current;
          ctx.dragId.current = null;
          if (dragged && dragged !== user.id) ctx.onReparent(dragged, user.id);
        }}
      >
        {hasKids ? (
          <button
            type="button"
            className="node-toggle"
            aria-expanded={!collapsed}
            aria-label={`${collapsed ? 'Expand' : 'Collapse'} reports of ${user.name}`}
            onClick={() => ctx.onToggle(user.id)}
          >
            {collapsed ? '+' : '–'}
          </button>
        ) : (
          <span className="node-spacer" />
        )}
        <div className="av" aria-hidden="true">
          {initials(user.name)}
        </div>
        <button
          type="button"
          className="card-btn"
          onClick={() => ctx.onEdit(user)}
          title="Edit this employee"
        >
          <span className="nm">
            {user.name}
            {isExecutiveUser(user) ? (
              <span className="tag-chip" style={{ marginLeft: 4 }}>
                Executive
              </span>
            ) : null}
          </span>
          <span className="ps" style={{ display: 'block' }}>
            {user.position} · {ACCESS_LABEL[user.systemAccess]}
            {hasKids ? ` · ${allKids.length} report${allKids.length === 1 ? '' : 's'}` : ''}
          </span>
        </button>
      </div>
      {!collapsed
        ? visibleKids.map((k) => <OrgNode key={k.id} user={k} ctx={ctx} seen={nextSeen} />)
        : null}
    </div>
  );
}

export interface OrgTreeProps {
  users: readonly User[];
  directory: Directory;
  /** '' means every department. */
  scopeDept: string;
  search: string;
  collapsed: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onEdit: (user: User) => void;
  onReparent: (employeeId: string, managerId: string) => void;
}

export function OrgTree({
  users,
  directory,
  scopeDept,
  search,
  collapsed,
  onToggle,
  onEdit,
  onReparent,
}: OrgTreeProps): ReactNode {
  const dragId = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const roots = useMemo(
    () =>
      users.filter((u) => {
        if (!u.active) return false;
        if (scopeDept && u.department !== scopeDept) return false;
        if (isExecutiveUser(u)) return true;
        const manager = u.reportsTo ? directory.byId(u.reportsTo) : undefined;
        return (
          !manager || !manager.active || (scopeDept !== '' && manager.department !== scopeDept)
        );
      }),
    [users, directory, scopeDept],
  );

  const ctx: TreeContext = {
    directory,
    scopeDept,
    search,
    collapsed,
    onToggle,
    onEdit,
    onReparent,
    dragId,
    dragOverId,
    setDragOverId,
  };

  const visibleRoots = search
    ? roots.filter((r) => subtreeMatches(directory, r, scopeDept, search, new Set<string>()))
    : roots;

  if (visibleRoots.length === 0) {
    return search ? (
      <EmptyState icon="🔍" title={`No employees match "${search}".`} />
    ) : (
      <EmptyState icon="🗂" title="No employees match this filter." />
    );
  }

  return (
    <div className="tree tree-root">
      {visibleRoots.map((r) => (
        <OrgNode key={r.id} user={r} ctx={ctx} seen={new Set<string>()} />
      ))}
    </div>
  );
}
